import { useEffect, useState } from 'react';

import { useFeatureFlag } from '@spotify/confidence-flag-evaluation-react';
import {
  OnboardingV1ManagedDataWarehouseProvisioningState as ProvisioningState,
  getTypeOrNull,
  useGetConfidenceCloudProvisioningQuery,
} from '@spotify/confidence-graphql';

const POLL_INTERVAL_MS = 15_000;

const MAX_POLL_TIME_MS = 900_000;

const MAX_NOT_PROVISIONED_POLL_TIME_MS = 90_000;

export const useConfidenceCloudProvisioning = ({
  skip = false,
}: { skip?: boolean } = {}) => {
  const [maxPollTimeReached, setMaxPollTimeReached] = useState(false);

  const { enabled } = useFeatureFlag('managed-warehouse', {
    enabled: false,
  });
  const { data, loading, startPolling, stopPolling } =
    useGetConfidenceCloudProvisioningQuery({
      skip: !enabled || skip,
      fetchPolicy: 'network-only',
    });

  const provisioning = getTypeOrNull(data?.managedDataWarehouseProvisioning);
  const state = provisioning?.state;
  const demoDataPending = provisioning?.demoDataPending ?? false;

  const shouldPoll =
    state === ProvisioningState.Creating ||
    state === ProvisioningState.Failed ||
    state === ProvisioningState.NotProvisioned ||
    demoDataPending;

  const pollBudgetMs =
    state === ProvisioningState.NotProvisioned
      ? MAX_NOT_PROVISIONED_POLL_TIME_MS
      : MAX_POLL_TIME_MS;

  useEffect(() => {
    if (!shouldPoll) {
      setMaxPollTimeReached(false);
      return undefined;
    }
    const timeoutId = window.setTimeout(
      () => setMaxPollTimeReached(true),
      pollBudgetMs,
    );
    return () => window.clearTimeout(timeoutId);
  }, [shouldPoll, pollBudgetMs]);

  useEffect(() => {
    if (!shouldPoll || maxPollTimeReached) {
      stopPolling();
      return undefined;
    }
    startPolling(POLL_INTERVAL_MS);
    return () => stopPolling();
  }, [shouldPoll, maxPollTimeReached, startPolling, stopPolling]);

  return {
    loading: enabled && loading,
    state,
    demoDataPending,
    pollingStopped: maxPollTimeReached,
  };
};
